import {
  Check,
  CheckCheck,
  Clock,
  AlertCircle,
  Image,
  Video,
  Mic,
  FileText,
  Sticker,
  MapPin,
  Sparkles,
  Monitor,
  Smartphone,
  type LucideIcon,
} from 'lucide-react';
import type { Message, MediaType, MessageStatus } from '@conversia/shared-types';
import { cn } from '@/lib/cn';
import { shortTime, dateTime } from '@/lib/format';
import { hashColor } from '@/lib/avatar';

/** Icono por tipo de media, para placeholders (`[Imagen]`, `[Audio]`, …). */
const MEDIA_ICONS: Record<MediaType, LucideIcon> = {
  IMAGE: Image,
  VIDEO: Video,
  AUDIO: Mic,
  DOCUMENT: FileText,
  STICKER: Sticker,
  LOCATION: MapPin,
};

/** Icono de estado de entrega para mensajes salientes (estilo WhatsApp). */
function StatusIcon({ status }: { status: MessageStatus }) {
  switch (status) {
    case 'PENDING':
      return <Clock className="h-3 w-3 opacity-70" aria-label="Pendiente" />;
    case 'SENT':
      return <Check className="h-3 w-3 opacity-70" aria-label="Enviado" />;
    case 'DELIVERED':
      return <CheckCheck className="h-3 w-3 opacity-70" aria-label="Entregado" />;
    case 'READ':
      return <CheckCheck className="h-3 w-3 text-sky-300" aria-label="Leído" />;
    case 'FAILED':
      return <AlertCircle className="h-3 w-3 text-red-300" aria-label="Falló el envío" />;
  }
}

/**
 * Identidad legible del remitente en grupos: su nombre/pushName o, si WhatsApp
 * no lo entregó, su número derivado del jid (`+549…`). Así la burbuja siempre
 * identifica quién habló, incluso sin pushName.
 */
function senderIdentity(message: Message): string | undefined {
  if (message.senderName) return message.senderName;
  if (message.senderPushName) return message.senderPushName;
  const digits = message.senderJid?.split('@')[0]?.replace(/\D/g, '');
  return digits ? `+${digits}` : undefined;
}

/**
 * Burbuja de mensaje. Cliente a la izquierda (gris); agente AI a la derecha
 * (marca); operador humano a la derecha (violeta); sistema centrado.
 *
 * En grupos (`showSender`), las burbujas entrantes muestran el nombre del
 * participante remitente, coloreado de forma estable por su identidad.
 */
import { horaDeMensaje, autorDeMensaje } from '@/lib/chat-timeline';
import { adSignalDeMensaje, tieneTarjetaDeAnuncio } from '@/lib/ad-signal';

/**
 * Quién escribió, como un ÍCONO + nombre en el mismo tono apagado que la hora.
 *
 * Antes eran chips con relleno sólido —blanco, celeste, amarillo— sobre la
 * burbuja violeta: en una conversación de veinte mensajes, veinte parches de
 * color que gritaban más que el texto (Esteban, 21-sep-2026: «que no cause
 * tanto ruido»). El dato sigue estando en cada burbuja, pero se lee cuando se
 * busca, no antes que el mensaje. El ícono distingue sin tener que leer:
 * destello = asistente, pantalla = alguien en el panel, teléfono = alguien
 * desde el celular.
 */
const AUTOR_ICONO: Record<string, LucideIcon> = {
  agente: Sparkles,
  panel: Monitor,
  movil: Smartphone,
};

/**
 * Tarjeta del anuncio (CTWA) del que vino el lead: título, copy y campaña.
 * En el teléfono el cliente la ve completa; acá el equipo necesita al menos
 * saber de qué publicidad llegó.
 */
function TarjetaAnuncio({ message }: { message: Message }) {
  const ad = adSignalDeMensaje(message);
  if (!ad) return null;
  return (
    // `break-words` en todo: el copy del anuncio lo escribe el anunciante y
    // puede traer un link o un hashtag larguísimo sin espacios. Sin esto, un
    // título de 150 caracteres estiraba la burbuja al doble de su ancho.
    <div className="mb-1.5 min-w-0 break-words rounded-lg border border-emerald-300 bg-emerald-50 p-2 dark:border-emerald-700/60 dark:bg-emerald-900/20">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
        Llegó por un anuncio
      </p>
      {ad.title && (
        <p className="mt-0.5 break-words text-xs font-semibold text-slate-800 dark:text-slate-100">
          {ad.title}
        </p>
      )}
      {ad.body && (
        <p className="break-words text-[11px] text-slate-600 dark:text-slate-300">{ad.body}</p>
      )}
      {(ad.sourceId || ad.sourceUrl) && (
        <p className="mt-1 break-words text-[10px] text-slate-500 dark:text-slate-400">
          {ad.sourceUrl ? (
            <a href={ad.sourceUrl} target="_blank" rel="noreferrer" className="underline">
              Ver anuncio
            </a>
          ) : null}
          {ad.sourceId ? <span className="ml-1">campaña {ad.sourceId}</span> : null}
        </p>
      )}
    </div>
  );
}

export function MessageBubble({
  message,
  showSender = false,
}: {
  message: Message;
  showSender?: boolean;
}) {
  if (message.role === 'SYSTEM') {
    return (
      <div className="my-2 flex justify-center">
        <span className="rounded-full px-3 py-1 text-xs text-slate-500 dark:text-slate-400">
          {message.content}
        </span>
      </div>
    );
  }

  const isClient = message.role === 'CLIENT';
  const isHuman = message.role === 'HUMAN' || message.role === 'HUMAN_MOBILE';
  const senderLabel = senderIdentity(message);
  const MediaIcon = message.mediaType ? MEDIA_ICONS[message.mediaType] : null;
  const autor = autorDeMensaje(message);

  return (
    <div className={cn('flex', isClient ? 'justify-start' : 'justify-end')}>
      <div
        title={dateTime(horaDeMensaje(message))}
        className={cn(
          'min-w-0 max-w-[75%] break-words rounded-[18px] px-3 py-1.5 text-sm',
          isClient && 'rounded-bl-[6px] bg-[var(--mac-burbuja)] text-slate-900 dark:text-slate-100',
          !isClient && !isHuman && 'rounded-br-[6px] bg-acento-lleno text-white',
          isHuman && 'rounded-br-[6px] bg-slate-700 text-white dark:bg-slate-600',
        )}
      >
        {showSender && isClient && senderLabel && (
          <p className="mb-0.5 text-xs font-semibold" style={{ color: hashColor(message.senderJid ?? senderLabel) }}>
            {senderLabel}
          </p>
        )}
        {message.mediaUrl && message.mediaType === 'IMAGE' ? (
          // Imagen inline; el wrapper <a> permite abrirla a tamaño completo.
          <a href={message.mediaUrl} target="_blank" rel="noreferrer" className="mb-1 block">
            <img
              src={message.mediaUrl}
              alt={message.content || 'Imagen'}
              loading="lazy"
              className="max-h-64 max-w-full rounded-lg"
            />
          </a>
        ) : message.mediaUrl && message.mediaType === 'AUDIO' ? (
          // Nota de voz / audio reproducible en el panel.
          <audio controls src={message.mediaUrl} className="mb-1 w-60 max-w-full" />
        ) : message.mediaUrl ? (
          // Resto de tipos (video, documento, etc.): link para abrir/descargar.
          <a
            href={message.mediaUrl}
            target="_blank"
            rel="noreferrer"
            className="mb-1 block text-xs underline opacity-80"
          >
            Adjunto
          </a>
        ) : null}
        {tieneTarjetaDeAnuncio(message) && <TarjetaAnuncio message={message} />}
        <p className="flex items-center gap-1.5 whitespace-pre-wrap break-words">
          {MediaIcon && <MediaIcon className="h-4 w-4 shrink-0 opacity-70" aria-hidden />}
          <span>{message.content}</span>
        </p>
        <span
          className={cn(
            'mt-0.5 flex items-center justify-end gap-1 whitespace-nowrap text-[10px]',
            isClient ? 'text-slate-500 dark:text-slate-400' : 'text-white/75',
          )}
        >
          {/* Quién escribió: agente, alguien del panel, o alguien desde el
              celular. Antes no se distinguían y el equipo no sabía si había
              contestado el bot o una persona. */}
          {autor &&
            (() => {
              const Icono = AUTOR_ICONO[autor.tipo];
              return (
                <>
                  <span className="inline-flex items-center gap-0.5">
                    {Icono && <Icono className="h-3 w-3" aria-hidden />}
                    {autor.etiqueta}
                  </span>
                  <span aria-hidden>·</span>
                </>
              );
            })()}
          {shortTime(horaDeMensaje(message))}
          {!isClient && <StatusIcon status={message.status} />}
        </span>
      </div>
    </div>
  );
}
